import type { components } from "@oathera/contract/api";
import type { ApiError } from "../../lib/api.ts";
import type { StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary and the write-validation model for the Alerts screen (task 11.13; design
 * 11 operator-API Alerts row, 12.2 Alerts row, 12.3 principles, 12.4 a11y). The FIRST screen with
 * WRITE operations, so this file is the single, testable home for:
 *
 * - the contract-derived types for every alerts operation (firing/history, ack, rules, silences,
 *   channels) — taken straight from `@oathera/contract/api` `components["schemas"]`, never redefined
 *   here (do NOT invent fields);
 * - the CRITICAL silence invariant ({@link validateSilenceInput}): a silence requires a NON-EMPTY
 *   reason and a BOUNDED duration (`ends_at > starts_at`, at most `max_silence_duration_s`, default
 *   30 days). This client check mirrors the server's three-place enforcement (design; FR-10.5) as
 *   UX / defence in depth; the server stays the authority and its 422/400 is still surfaced;
 * - the human labels, status-level mappings (icon+text, never colour alone; design 12.4) and honest
 *   formatters the regions share;
 * - the error-to-sentence helpers so a 403 reads "the server did not permit this" and a 400/422 the
 *   silence invariant reads as the server's own validation message.
 *
 * It defines no API shapes and does no fetching (that is `use-alerts.ts`).
 */

type Schemas = components["schemas"];

/* ---- Contract-derived types ----------------------------------------------------------------- */

/** One alert (firing/pending/resolved) with its ack overlay and the silences covering it. */
export type Alert = Schemas["Alert"];
/** One page of alerts (`GET /alerts`). */
export type AlertPage = Schemas["AlertPage"];
/** The lifecycle state of an alert (`acked` is an overlay, not a state). */
export type AlertState = Schemas["AlertState"];
/** The alert severity ladder. */
export type AlertSeverity = Schemas["AlertSeverity"];
/** The ack request body (reason mandatory; optional bounded `expires_in_s`). */
export type AlertAckRequest = Schemas["AlertAckRequest"];
/** The ack result (updated alert + audit receipt). */
export type AlertAckResult = Schemas["AlertAckResult"];

/** One alert rule (built-in pack included). */
export type AlertRule = Schemas["AlertRule"];
/** One page of rules (`GET /alerts/rules`). */
export type AlertRulePage = Schemas["AlertRulePage"];
/** The create/replace rule body. */
export type AlertRuleInput = Schemas["AlertRuleInput"];
/** The create/replace rule result. */
export type AlertRuleResult = Schemas["AlertRuleResult"];
/** The rule evaluation kind. */
export type RuleKind = Schemas["RuleKind"];
/** The rule definition (which members a kind needs is checked server-side against the catalog). */
export type RuleDefinition = Schemas["RuleDefinition"];

/** One silence (matcher + reason + bounded interval). */
export type Silence = Schemas["Silence"];
/** One page of silences, carrying `max_silence_duration_s` (the invariant bound). */
export type SilencePage = Schemas["SilencePage"];
/** The create/replace silence body (reason + bounded interval required). */
export type SilenceInput = Schemas["SilenceInput"];
/** The silence matcher (rule_id / severity / labels; at least one). */
export type SilenceMatcher = Schemas["SilenceMatcher"];
/** The create/replace silence result. */
export type SilenceResult = Schemas["SilenceResult"];
/** The remove-silence result (id + removed:true + audit). */
export type SilenceRemoved = Schemas["SilenceRemoved"];

/** One notification channel (webhook) with delivery status. */
export type AlertChannel = Schemas["AlertChannel"];
/** One page of channels (`GET /alerts/channels`). */
export type AlertChannelPage = Schemas["AlertChannelPage"];
/** The create-channel body (carries the write-only `signing_secret`). */
export type AlertChannelInput = Schemas["AlertChannelInput"];
/** The create-channel result (channel; the one-time secret is NOT echoed by the server). */
export type AlertChannelResult = Schemas["AlertChannelResult"];

/** A non-empty operator reason (recorded in `operator_audit`). */
export type OperatorReason = Schemas["OperatorReason"];
/** The audit receipt a write appends. */
export type AuditReceipt = Schemas["AuditReceipt"];

/* ---- Enum option sets (straight from the contract) ------------------------------------------ */

/** The alert states the history filter offers. */
export const ALERT_STATE_OPTIONS: readonly AlertState[] = ["pending", "firing", "resolved"];
/** The alert severities a rule/filter offers. */
export const ALERT_SEVERITY_OPTIONS: readonly AlertSeverity[] = ["info", "warning", "critical"];
/** The rule kinds a new rule can declare. */
export const RULE_KIND_OPTIONS: readonly RuleKind[] = [
  "rate_threshold",
  "ratio_threshold",
  "baseline_deviation",
  "event_match",
  "absence",
  "audit_status",
];
/** The comparison operators a threshold rule can use (from `RuleDefinition.op`). */
export const RULE_OP_OPTIONS = [">", ">=", "<", "<=", "==", "!="] as const;
export type RuleOp = (typeof RULE_OP_OPTIONS)[number];

/* ---- The silence invariant: reason + bounded duration (the keystone of this screen) --------- */

/**
 * The default longest allowed silence, matching the contract's `SilencePage.max_silence_duration_s`
 * default (30 days in seconds). The screen prefers the value the server returns on the silence page,
 * falling back to this when the page is not loaded yet.
 */
export const DEFAULT_MAX_SILENCE_DURATION_S = 2_592_000;

/** A validated silence form, ready to send, plus the fields a UI binds to. */
export interface SilenceFormValues {
  /** The matcher the operator built (at least one of rule_id / severity / labels). */
  readonly matcher: SilenceMatcher;
  /** The free-text reason (recorded in audit). Must be non-empty after trimming. */
  readonly reason: string;
  /** Start of the silence window, epoch ms. */
  readonly startsAtMs: number;
  /** End of the silence window, epoch ms. Must be after start and within the bound. */
  readonly endsAtMs: number;
}

/** A single field-level validation problem, keyed so the form can show it next to the control. */
export interface SilenceValidationIssue {
  readonly field: "matcher" | "reason" | "starts_at" | "ends_at" | "duration";
  readonly message: string;
}

/** The result of validating a silence form client-side. */
export interface SilenceValidation {
  /** True when the form may be submitted (the server is still the final authority). */
  readonly ok: boolean;
  /** Field-level problems; empty when `ok`. */
  readonly issues: readonly SilenceValidationIssue[];
  /** The contract body to send when `ok`; undefined otherwise. */
  readonly input?: SilenceInput;
}

/**
 * Validate a silence form against the invariant the server enforces (design; FR-10.5), BEFORE the
 * API is called. This is the client half of the "block submit without a reason and a valid bounded
 * duration" requirement — the submit button is disabled while this returns `ok === false`, and the
 * reasons are shown inline. It intentionally mirrors the contract's `SilenceInput` rules:
 *
 * - `matcher` must carry at least one of rule_id / severity / labels (contract `minProperties: 1`);
 * - `reason` must be non-empty after trimming (contract `OperatorReason`: `minLength 1`, `pattern
 *   \S`) and within 256 chars;
 * - `starts_at` and `ends_at` must both be finite timestamps and `ends_at` must be strictly AFTER
 *   `starts_at` (a zero or negative interval is unbounded/invalid);
 * - the interval must be AT MOST `maxDurationS` (default 30 days) — an over-long silence is rejected.
 *
 * The server re-checks all of this and may still reject (e.g. a tighter tenant bound or a matcher
 * the catalog refuses); `use-alerts.ts` surfaces that 400/422 verbatim. This never widens what the
 * server allows — it only blocks a request that is certain to be refused and explains why.
 */
export function validateSilenceInput(
  values: SilenceFormValues,
  maxDurationS: number = DEFAULT_MAX_SILENCE_DURATION_S,
): SilenceValidation {
  const issues: SilenceValidationIssue[] = [];

  if (!matcherHasAnyField(values.matcher)) {
    issues.push({
      field: "matcher",
      message: "Add at least one matcher (a rule, a severity, or a label) so the silence targets something.",
    });
  }

  const reason = values.reason.trim();
  if (reason.length === 0) {
    issues.push({ field: "reason", message: "A reason is required and recorded in the audit log." });
  } else if (reason.length > 256) {
    issues.push({ field: "reason", message: "The reason must be at most 256 characters." });
  }

  const startOk = Number.isFinite(values.startsAtMs);
  const endOk = Number.isFinite(values.endsAtMs);
  if (!startOk) issues.push({ field: "starts_at", message: "Enter a valid start time." });
  if (!endOk) issues.push({ field: "ends_at", message: "Enter a valid end time." });

  if (startOk && endOk) {
    const durationMs = values.endsAtMs - values.startsAtMs;
    if (durationMs <= 0) {
      issues.push({
        field: "duration",
        message: "The end must be after the start. A silence must be bounded, not open-ended.",
      });
    } else if (durationMs > maxDurationS * 1000) {
      issues.push({
        field: "duration",
        message: `The silence is too long. The maximum is ${formatDurationSeconds(maxDurationS)}.`,
      });
    }
  }

  if (issues.length > 0) return { ok: false, issues };

  const input: SilenceInput = {
    matcher: normaliseMatcher(values.matcher),
    reason,
    starts_at: values.startsAtMs,
    ends_at: values.endsAtMs,
  };
  return { ok: true, issues: [], input };
}

/** True when a matcher carries at least one of its three fields (contract `minProperties: 1`). */
export function matcherHasAnyField(matcher: SilenceMatcher): boolean {
  const hasRule = typeof matcher.rule_id === "string" && matcher.rule_id.length > 0;
  const hasSeverity = typeof matcher.severity === "string";
  const hasLabels = matcher.labels !== undefined && Object.keys(matcher.labels).length > 0;
  return hasRule || hasSeverity || hasLabels;
}

/** Drop empty matcher members so the body matches the contract (no empty string / empty labels). */
function normaliseMatcher(matcher: SilenceMatcher): SilenceMatcher {
  const next: SilenceMatcher = {};
  if (typeof matcher.rule_id === "string" && matcher.rule_id.length > 0) next.rule_id = matcher.rule_id;
  if (typeof matcher.severity === "string") next.severity = matcher.severity;
  if (matcher.labels !== undefined && Object.keys(matcher.labels).length > 0) next.labels = matcher.labels;
  return next;
}

/* ---- The acknowledgement reason (also mandatory; FR-10.5) ----------------------------------- */

/** Validate an ack reason client-side (mandatory, non-empty, within 256 chars). */
export function validateAckReason(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return "A reason is required to acknowledge an alert.";
  if (trimmed.length > 256) return "The reason must be at most 256 characters.";
  return null;
}

/* ---- Status levels (icon + text, never colour alone; design 12.4) --------------------------- */

/**
 * The status level for an alert, folding state and severity. A FIRING alert is prominent (an error
 * level for critical, a warn for info/warning); a pending alert warns; a resolved alert is neutral.
 * This is the shared icon+text vocabulary — never colour alone (design 12.4) — and nothing here is
 * ever "healthy/green", because a firing alert is not a green state (design 12.3).
 */
export function alertLevel(alert: Alert): StatusLevel {
  if (alert.state === "resolved") return "neutral";
  if (alert.state === "pending") return "warn";
  // firing:
  return alert.severity === "critical" ? "error" : "warn";
}

/** The status level for a severity on its own (used for the rules/severity badges). */
export function severityLevel(severity: AlertSeverity): StatusLevel {
  switch (severity) {
    case "info":
      return "neutral";
    case "warning":
      return "warn";
    case "critical":
      return "error";
  }
}

/** The status level for a channel's delivery status (never green: a channel is infra, not health). */
export function deliveryLevel(status: AlertChannel["delivery"]["status"]): StatusLevel {
  switch (status) {
    case "ok":
      return "neutral";
    case "never":
      return "unknown";
    case "retrying":
      return "warn";
    case "failed":
      return "error";
  }
}

/* ---- Human labels --------------------------------------------------------------------------- */

export function alertStateLabel(state: AlertState): string {
  switch (state) {
    case "pending":
      return "Pending";
    case "firing":
      return "Firing";
    case "resolved":
      return "Resolved";
  }
}

export function severityLabel(severity: AlertSeverity): string {
  switch (severity) {
    case "info":
      return "Info";
    case "warning":
      return "Warning";
    case "critical":
      return "Critical";
  }
}

export function ruleKindLabel(kind: RuleKind): string {
  switch (kind) {
    case "rate_threshold":
      return "Rate threshold";
    case "ratio_threshold":
      return "Ratio threshold";
    case "baseline_deviation":
      return "Baseline deviation";
    case "event_match":
      return "Event match";
    case "absence":
      return "Absence";
    case "audit_status":
      return "Audit status";
  }
}

export function deliveryStatusLabel(status: AlertChannel["delivery"]["status"]): string {
  switch (status) {
    case "never":
      return "Never delivered";
    case "ok":
      return "Delivering";
    case "retrying":
      return "Retrying";
    case "failed":
      return "Failed";
  }
}

/* ---- Honest formatters ---------------------------------------------------------------------- */

/** Format an epoch-ms timestamp as a local time, or "n/a" when null/unknown. */
export function formatTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "n/a";
  return new Date(ms).toLocaleString();
}

/** Format an alert's current value, or "n/a" when the server could not compute one. */
export function formatValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "n/a";
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

/** Format a seconds duration as a short human span (e.g. "30 d", "6 h 00 m", "45 s"). */
export function formatDurationSeconds(totalS: number): string {
  if (!Number.isFinite(totalS) || totalS < 0) return "n/a";
  const s = Math.floor(totalS);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m ${String(s % 60).padStart(2, "0")} s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${String(m % 60).padStart(2, "0")} m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `${d} d` : `${d} d ${String(remH).padStart(2, "0")} h`;
}

/** Format a label map as "k=v, k=v" for a dense cell, or "n/a" when empty. */
export function formatLabels(labels: Readonly<Record<string, string>> | undefined): string {
  if (labels === undefined) return "n/a";
  const entries = Object.entries(labels);
  if (entries.length === 0) return "n/a";
  return entries.map(([k, v]) => `${k}=${v}`).join(", ");
}

/* ---- Matcher summary ------------------------------------------------------------------------ */

/** A readable one-line summary of a silence matcher for the list and the confirm dialog. */
export function matcherSummary(matcher: SilenceMatcher): string {
  const parts: string[] = [];
  if (matcher.rule_id !== undefined && matcher.rule_id.length > 0) parts.push(`rule ${matcher.rule_id}`);
  if (matcher.severity !== undefined) parts.push(`severity ${severityLabel(matcher.severity)}`);
  if (matcher.labels !== undefined && Object.keys(matcher.labels).length > 0) {
    parts.push(formatLabels(matcher.labels));
  }
  return parts.length > 0 ? parts.join(" · ") : "(no matcher)";
}

/* ---- Label-map parsing (for the silence matcher + rule where/group_by inputs) --------------- */

/**
 * Parse a "k=v" per-line (or comma-separated) textarea into a label map, ignoring blank lines. A
 * line without an `=` is treated as a key with an empty value, which the caller can reject; here we
 * keep only well-formed `k=v` pairs so a half-typed line does not break the matcher.
 */
export function parseLabelMap(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  const tokens = text.split(/[\n,]/);
  for (const token of tokens) {
    const trimmed = token.trim();
    if (trimmed.length === 0) continue;
    const eq = trimmed.indexOf("=");
    if (eq <= 0) continue;
    const key = trimmed.slice(0, eq).trim();
    const value = trimmed.slice(eq + 1).trim();
    if (key.length === 0) continue;
    out[key] = value;
  }
  return out;
}

/* ---- Error → sentence ----------------------------------------------------------------------- */

/**
 * Turn a failed WRITE into an operator-facing sentence. A 403 reads as "the server did not permit
 * this" (the role was insufficient — the server is the authority even when the UI hid the control);
 * a 400/422 is the server's own validation message shown verbatim (so the silence invariant message
 * the server returns is what the operator sees); a 409 is a conflict; a waking core defers to the
 * shell. This is the write-side counterpart of the read-side error map.
 */
export function writeErrorMessage(error: ApiError, action: string): string {
  switch (error.kind) {
    case "forbidden":
      return `The server did not permit this ${action}. It requires the operator role; your role could not perform it.`;
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "not_found":
      return `The ${action} target no longer exists. Refresh and try again.`;
    case "network":
      return `The ${action} request could not be sent. Check your connection and try again.`;
    case "waking":
      return "The control plane is waking. The request will be retried shortly.";
    case "request":
    case "server":
    case "malformed":
    default:
      // A 400/422 carries the server's validation message (e.g. the silence reason/bounded-duration
      // rule, or a rule naming an uncatalogued metric) — show it so the operator sees exactly why.
      return error.message;
  }
}

/** Turn a failed READ into an operator-facing sentence (mirrors the other screens' read map). */
export function readErrorMessage(error: ApiError | null): string {
  if (error === null) return "The alerts view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see alerts. The server enforces access.";
    case "network":
      return "The alerts request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}
